'use client';
// ──────────────────────────────────────────────────────
// AdminShell — Collapsible sidebar + content layout
// White enterprise theme, RTL Arabic, brand-consistent
// ──────────────────────────────────────────────────────
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';

interface AdminShellProps {
    children: React.ReactNode;
    onLogout: () => void;
}

const STORAGE_KEY = 'qcare_admin_sidebar_collapsed';

const navSections = [
    {
        title: 'المنتجات',
        items: [
            {
                href: '/admin/products',
                label: 'قائمة المنتجات',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                ),
            },
            {
                href: '/admin/products/canonical',
                label: 'تصنيف المنتجات',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 12l2 2 4-4" />
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    </svg>
                ),
            },
            {
                href: '/admin/products/migration-review',
                label: 'مراجعة الهجرة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                ),
            },
            {
                href: '/admin/sync',
                label: 'مزامنة البيانات',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0118.8-4.3M22 12.5a10 10 0 01-18.8 4.2" />
                    </svg>
                ),
            },
        ],
    },
    {
        title: 'المحادثات',
        items: [
            {
                href: '/admin/customers',
                label: 'العملاء',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4-4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <path d="M23 21v-2a4 4 0 00-3-3.87" />
                        <path d="M16 3.13a4 4 0 010 7.75" />
                    </svg>
                ),
            },
            {
                href: '/admin/intelligence/sessions',
                label: 'جلسات المحادثة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                ),
            },
        ],
    },
    {
        title: 'تدريب QAI',
        items: [
            {
                href: '/admin/knowledge/qa',
                label: 'التدريب والمعرفة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                ),
            },
            {
                href: '/admin/knowledge/aliases',
                label: 'الأسماء الدارجة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 7h18M3 12h18M3 17h12" />
                    </svg>
                ),
            },
        ],
    },
    {
        title: 'التحليلات',
        items: [
            {
                href: '/admin/intelligence',
                label: 'نظرة عامة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" />
                    </svg>
                ),
            },
            {
                href: '/admin/intelligence/products',
                label: 'تحليل المنتجات',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                        <polyline points="16 7 22 7 22 13" />
                    </svg>
                ),
            },
        ],
    },
    {
        title: 'العمليات',
        items: [
            {
                href: '/admin/operations/ai-control',
                label: 'التحكم بالذكاء',
                icon: (
                    // Power/control icon — signals "operator runtime
                    // controls" without overloading the existing
                    // settings/cog metaphor.
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 2v10" />
                        <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
                    </svg>
                ),
            },
        ],
    },
    {
        title: 'الإعدادات',
        items: [
            {
                href: '/admin/kiosks',
                label: 'الأجهزة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="2" width="16" height="16" rx="2" />
                        <path d="M9 22h6" />
                        <path d="M12 18v4" />
                    </svg>
                ),
            },
            {
                href: '/admin/access/users',
                label: 'المستخدمون',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
                        <circle cx="9" cy="7" r="4" />
                        <circle cx="19" cy="11" r="2" />
                        <path d="M19 8v1m0 4v1m2.6-4.5l-.9.5m-5.4 3l-.9.5m6.3 0l-.9-.5m-5.4-3l-.9-.5" />
                    </svg>
                ),
            },
            {
                href: '/admin/access/roles',
                label: 'الأدوار والصلاحيات',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    </svg>
                ),
            },
            {
                href: '/admin/trust',
                label: 'ضمان الأصالة',
                icon: (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        <path d="M9 12l2 2 4-4" />
                    </svg>
                ),
            },
        ],
    },
];

export function AdminShell({ children, onLogout }: AdminShellProps) {
    const pathname = usePathname();
    const [collapsed, setCollapsed] = useState(() => {
        try {
            return typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'true';
        } catch { return false; }
    });
    const [mounted] = useState(() => typeof window !== 'undefined');

    function toggleCollapse() {
        setCollapsed(prev => {
            const next = !prev;
            try { localStorage.setItem(STORAGE_KEY, String(next)); } catch { /* noop */ }
            return next;
        });
    }

    // Prevent flash of wrong width before hydration
    const sidebarWidth = !mounted ? 260 : collapsed ? 72 : 260;

    return (
        <div className="admin-rtl h-dvh flex" style={{ background: '#f8f9fb' }}>
            {/* ── Sidebar ─────────────────────────────────── */}
            <aside
                className="flex flex-col flex-shrink-0 overflow-hidden border-s"
                style={{
                    width: sidebarWidth,
                    height: '100dvh',
                    position: 'sticky',
                    top: 0,
                    background: '#ffffff',
                    borderColor: '#e5e7eb',
                    transition: mounted ? 'width 200ms ease' : 'none',
                }}
            >
                {/* Brand header */}
                <div className="flex items-center gap-3 border-b px-4"
                    style={{ borderColor: '#e5e7eb', height: 64 }}>
                    <Link href="/admin/products" className="flex items-center gap-3 min-w-0">
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src="/branding/qio-logo.jpeg"
                            alt="QIO"
                            width={36}
                            height={36}
                            className="rounded-lg flex-shrink-0"
                            style={{ border: '1px solid #e5e7eb' }}
                        />
                        {!collapsed && (
                            <div className="min-w-0">
                                <div className="text-sm font-bold truncate" style={{ color: '#1a1a2e' }}>
                                    لوحة تحكم Q Care
                                </div>
                                <div className="text-[11px] truncate" style={{ color: '#9ca3af' }}>
                                    إدارة المنتجات
                                </div>
                            </div>
                        )}
                    </Link>
                    {/* Collapse toggle — pushed to end */}
                    <button
                        onClick={toggleCollapse}
                        title={collapsed ? 'توسيع القائمة' : 'تصغير القائمة'}
                        className="flex-shrink-0 w-7 h-7 rounded-md flex items-center justify-center transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        style={{
                            marginInlineStart: collapsed ? 0 : 'auto',
                            color: '#6b7280',
                            background: 'transparent',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                            style={{ transform: collapsed ? 'scaleX(-1)' : 'none', transition: 'transform 200ms ease' }}>
                            <path d="M11 19l-7-7 7-7" />
                            <path d="M18 19l-7-7 7-7" />
                        </svg>
                    </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
                    {navSections.map((section) => (
                        <div key={section.title}>
                            {!collapsed && (
                                <div className="text-[11px] font-semibold uppercase tracking-wider mb-2 px-3"
                                    style={{ color: '#9ca3af' }}>
                                    {section.title}
                                </div>
                            )}
                            <div className="space-y-0.5">
                                {section.items.map((item) => {
                                    const isActive = item.href === '/admin/products'
                                        ? pathname === '/admin/products' || /^\/admin\/products\/[^c]/.test(pathname)
                                        : pathname.startsWith(item.href);
                                    return (
                                        <Link
                                            key={item.href}
                                            href={item.href}
                                            title={collapsed ? item.label : undefined}
                                            className="flex items-center gap-3 rounded-lg text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                                            style={{
                                                padding: collapsed ? '10px 0' : '10px 12px',
                                                justifyContent: collapsed ? 'center' : 'flex-start',
                                                color: isActive ? '#0ea5a5' : '#374151',
                                                background: isActive ? 'rgba(14, 165, 165, 0.06)' : 'transparent',
                                                borderInlineEnd: isActive ? '3px solid #0ea5a5' : '3px solid transparent',
                                            }}
                                            onMouseEnter={e => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = '#f3f4f6';
                                                    e.currentTarget.style.color = '#111827';
                                                }
                                            }}
                                            onMouseLeave={e => {
                                                if (!isActive) {
                                                    e.currentTarget.style.background = 'transparent';
                                                    e.currentTarget.style.color = '#374151';
                                                }
                                            }}
                                        >
                                            <span className="flex-shrink-0">{item.icon}</span>
                                            {!collapsed && <span className="truncate">{item.label}</span>}
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer: Logout */}
                <div className="shrink-0 border-t px-3 py-3" style={{ borderColor: '#e5e7eb' }}>
                    <button
                        onClick={onLogout}
                        title={collapsed ? 'تسجيل الخروج' : undefined}
                        className="flex items-center gap-3 rounded-lg text-sm font-medium transition-colors w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
                        style={{
                            padding: collapsed ? '10px 0' : '10px 12px',
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            color: '#6b7280',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fef2f2'; }}
                        onMouseLeave={e => { e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.background = 'transparent'; }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0">
                            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
                            <polyline points="16,17 21,12 16,7" />
                            <line x1="21" y1="12" x2="9" y2="12" />
                        </svg>
                        {!collapsed && <span>تسجيل الخروج</span>}
                    </button>
                </div>
            </aside>

            {/* ── Main Content ────────────────────────────── */}
            <main className="flex-1 overflow-y-auto">
                <div className="max-w-[1600px] mx-auto px-6 py-8">
                    {children}
                </div>
            </main>
        </div>
    );
}
